import { useTranslation } from 'react-i18next';
import { Box, Layers, Square } from 'lucide-react';

import { cn } from '../../lib/cn';
import type { BoxRenderMode } from './CanvasStage';

/**
 * RenderModeToggle — bottom-right floating segmented control. Lets the user
 * pick how bounding boxes render on the canvas:
 *
 *   ┌─ Outline ─┬─ Shaded ─┬─ Both ─┐
 *   │  [□]      │  [▣]     │  [▤]   │
 *   └───────────┴──────────┴────────┘
 *
 * Standard pattern in annotation tools (CVAT, Roboflow Annotate) — outline-
 * only by default lets users see the underlying image clearly; shaded helps
 * when boxes are big and class color matters more than precise edges; both
 * is a compromise.
 *
 * Persisted in localStorage so the choice survives reloads. NOT per-project
 * — same preference everywhere in the app.
 */
interface RenderModeToggleProps {
    isDarkMode?: boolean;
    value: BoxRenderMode;
    onChange: (mode: BoxRenderMode) => void;
}

export default function RenderModeToggle({ isDarkMode = false, value, onChange }: RenderModeToggleProps) {
    const { t } = useTranslation('canvas');

    const items: Array<{ key: BoxRenderMode; icon: React.ComponentType<{ className?: string }>; label: string }> = [
        { key: 'outline', icon: Square, label: t('render_outline', { defaultValue: 'Outline' }) },
        { key: 'shaded', icon: Box, label: t('render_shaded', { defaultValue: 'Shaded' }) },
        { key: 'both', icon: Layers, label: t('render_both', { defaultValue: 'Both' }) },
    ];

    return (
        <div
            className={cn(
                'absolute bottom-4 right-4 z-20 flex items-center rounded-full backdrop-blur-sm border shadow-lg p-0.5',
                isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200',
            )}
            role="group"
            aria-label={t('render_mode', { defaultValue: 'Box rendering mode' })}
        >
            {items.map(({ key, icon: Icon, label }) => {
                const active = key === value;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onChange(key)}
                        title={label}
                        aria-label={label}
                        aria-pressed={active}
                        className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-full transition-colors duration-150',
                            active
                                ? isDarkMode
                                    ? 'bg-primary-500/20 text-primary-300'
                                    : 'bg-primary-50 text-primary-700'
                                : isDarkMode
                                    ? 'text-gray-400 hover:bg-gray-800'
                                    : 'text-gray-500 hover:bg-gray-100',
                        )}
                    >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                    </button>
                );
            })}
        </div>
    );
}
